/**
 * Dinero en el panel (componentes.md §21): la API manda céntimos con su moneda (`Money`) y el panel solo los **escribe**
 * («1.284,50 €», siempre con punto de miles y dos decimales) o lee lo que la persona teclea en un campo de importe
 * («12,50» → 1250). **Nunca calcula** cargos, netos, comisiones ni lo que queda por devolver: eso lo da la API
 * (`refundable_amount`, `net`, `previous`…; G282, G290).
 */
export interface Importe {
    amount: number;
    currency: string;
}

const formatos = new Map<string, Intl.NumberFormat>();

function formato(moneda: string): Intl.NumberFormat {
    let f = formatos.get(moneda);
    if (f === undefined) {
        try {
            f = new Intl.NumberFormat('es-ES', { style: 'currency', currency: moneda, minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: 'always' });
        } catch {
            f = new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: 'always' });
        }
        formatos.set(moneda, f);
    }
    return f;
}

/** «1.284,50 €» (vacío si no hay importe). El signo menos, el tipográfico («−15,00 €»). */
export function dinero(importe: Importe | null | undefined): string {
    if (importe == null) {
        return '';
    }
    return formato(importe.currency.toUpperCase()).format(importe.amount / 100).replace('-', '−').replace(/\u00a0/g, ' ');
}

/** «1.284,50» sin la moneda (para la cifra grande, con la moneda aparte). */
export function cifraDinero(importe: Importe): string {
    return (importe.amount / 100).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: 'always' }).replace('-', '−');
}

/** Símbolo de la moneda («€»), para la etiqueta de un campo de importe. */
export function simboloMoneda(moneda: string): string {
    try {
        return new Intl.NumberFormat('es-ES', { style: 'currency', currency: moneda.toUpperCase() }).formatToParts(0).find((p) => p.type === 'currency')?.value ?? moneda;
    } catch {
        return moneda;
    }
}

/** El error de formato de un campo de importe (null si está bien, o vacío y se admite vacío). */
export function errorDeImporte(valor: string, obligatorio: boolean): string | null {
    if (valor.trim() === '') {
        return obligatorio ? 'Escribe el importe.' : null;
    }
    const c = aCentimos(valor);
    if (c === null) {
        return 'Escribe un importe, por ejemplo 10 o 12,50.';
    }
    return obligatorio && c === 0 ? 'El importe tiene que ser mayor que 0.' : null;
}

/**
 * Lo tecleado en un campo de importe, en céntimos: «12», «12,5», «12,50», «1.284,50» o «12.50». null si no es un importe
 * válido (vacío, letras, más de dos decimales o negativo).
 */
export function aCentimos(texto: string): number | null {
    let t = texto.replace(/\s|€/g, '');
    if (t === '') {
        return null;
    }
    if (t.includes(',')) {
        t = t.replace(/\./g, '').replace(',', '.');
    } else if (/^\d{1,3}(\.\d{3})+$/.test(t)) {
        // «1.284» es mil doscientos ochenta y cuatro, no uno con veintiocho.
        t = t.replace(/\./g, '');
    }
    if (!/^\d+(\.\d{1,2})?$/.test(t)) {
        return null;
    }
    const [enteros = '0', decimales = ''] = t.split('.');
    return Number(enteros) * 100 + Number(decimales.padEnd(2, '0'));
}

/** Céntimos a lo que se escribe en el campo de importe («12,50»; vacío con 0 si `vacioConCero`). */
export function textoDeCentimos(centimos: number | null | undefined, vacioConCero = false): string {
    if (centimos == null || (vacioConCero && centimos === 0)) {
        return '';
    }
    return (centimos / 100).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });
}
